import { useEffect, useState } from "react"

import { Button, Divider, Dropdown, Flex, Spin, Tooltip, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"

import Private from "../../../../../Components/Private"
import Icon from "../../../../../Icon"
import { formatCurrency } from "../../../../../Utils/utils"
import ReferralAction from "../../../reducer"
import CommissionPerAlertBlock from "../CommissionPerAlertBlock/CommissionPerAlertBlock"
import PaymentMethodDetailsModal from "../PaymentMethodDetailsModal/PaymentMethodDetailsModal"
import PaymentMethodFormModal from "../PaymentMethodFormModal/PaymentMethodFormModal"
import WithdrawFundsModal from "../WithdrawFundsModal/WithdrawFundsModal"

import "./styles.less"

const { Text } = Typography

const MINIMAL_WITHDRAWAL_CENTS = 10000

const EarningsStats = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const { outsiderBlocks, outsiderBlocksFetching } = useSelector((state) => state.referrals)
  const { isAdmin } = useSelector((state) => state.auth)
  const { isMobile } = useSelector((state) => state.nav)
  const adminParams = isAdmin && id ? { user_id: id } : {}

  useEffect(() => {
    dispatch(ReferralAction.getOutsiderBlocksRequest(adminParams))
  }, [])

  const isPendingWithdrawals = outsiderBlocks?.pending_withdrawals_cents > 0
  const isLessThanMinimum = outsiderBlocks?.available_funds_cents < MINIMAL_WITHDRAWAL_CENTS

  const isWithdrawDisabled = isPendingWithdrawals || isLessThanMinimum

  return (
    <Spin spinning={outsiderBlocksFetching}>
      <Flex vertical gap={16} className="earnings-stats">
        <Flex gap={isMobile ? 16 : 24} wrap="wrap">
          <StatsBlock
            title="Total available funds"
            total={outsiderBlocks?.available_funds_cents ?? 0}
            totalCommissions={outsiderBlocks?.available_funds_cents ?? 0}
          />
          <StatsBlock
            title="Pending withdrawals"
            total={outsiderBlocks?.pending_withdrawals_cents ?? 0}
            totalCommissions={outsiderBlocks?.pending_withdrawals_cents ?? 0}
          />
          <StatsBlock
            title="Paid commissions"
            total={outsiderBlocks?.paid_commissions_cents ?? 0}
            totalCommissions={outsiderBlocks?.paid_commissions_cents ?? 0}
          />
          <WithdrawBlock
            isAdmin={isAdmin}
            paymentMethod={outsiderBlocks?.payout_method}
            disabled={isWithdrawDisabled}
            disabledTooltip="You must have at least $100 in your balance and your previous transaction must be completed to request a withdrawal"
          />
        </Flex>
        {!isMobile && (
          <CommissionPerAlertBlock
            ethoca={outsiderBlocks?.commission_per_alert_ethoca_cents ?? 0}
            verifi={outsiderBlocks?.commission_per_alert_verify_cents ?? 0}
          />
        )}
      </Flex>
    </Spin>
  )
}

const StatsBlock = ({ title, total, totalCommissions }) => {
  return (
    <Flex vertical className="referral-outsiders-card" gap={12}>
      <Flex vertical gap={8}>
        <Text strong>{title}</Text>
        <Text className="value-large">
          <Private>{formatCurrency(total / 100)}</Private>
        </Text>
      </Flex>
      <div>
        <Flex justify="space-between" gap={10}>
          <Text strong type="secondary">
            Total commissions
          </Text>
          <Text strong>
            <Private>{formatCurrency(totalCommissions / 100)}</Private>
          </Text>
        </Flex>
        <Divider className="divider" />
        <Flex justify="space-between" gap={10}>
          <Text strong type="secondary">
            Total
          </Text>
          <Text strong>
            <Private>{formatCurrency(total / 100)}</Private>
          </Text>
        </Flex>
      </div>
    </Flex>
  )
}

const WithdrawBlock = ({ isAdmin, paymentMethod, disabledTooltip, disabled }) => {
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [paymentMethodDropdownOpened, setPaymentMethodDropdownOpened] = useState(false)
  const [paymentMethodFormType, setPaymentMethodFormType] = useState(null)
  const [paymentMethodDetailsOpened, setPaymentMethodDetailsOpened] = useState(false)
  const [paymentMethodFormOpened, setPaymentMethodFormOpened] = useState(false)

  const openPaymentMethodForm = (type) => {
    setPaymentMethodDropdownOpened(false)
    setPaymentMethodFormOpened(true)
    setPaymentMethodFormType(type)
  }

  const openPaymentMethodDetails = () => {
    setPaymentMethodDropdownOpened(false)
    setPaymentMethodDetailsOpened(true)
  }
  return (
    <Flex className="referral-outsiders-card" vertical gap={16} justify="space-between">
      <Flex gap={12} vertical>
        <Text strong>Withdraw funds to</Text>
        <div className="withdraw-info">
          <Text type="secondary" strong>
            Account currency
          </Text>
          <Text strong>{paymentMethod?.currency ?? "-"}</Text>
          <Text type="secondary" strong>
            {paymentMethod?.country === "US" ? "Account number" : "IBAN"}
          </Text>
          <Text strong>{paymentMethod?.masked_identifier ?? "-"}</Text>
        </div>
      </Flex>

      {paymentMethod ? (
        <Flex gap={12}>
          {disabled && disabledTooltip ? (
            <Tooltip title={disabledTooltip}>
              <Button
                className="btn withdraw"
                type="primary"
                onClick={() => setShowWithdrawModal(true)}
                disabled={disabled || isAdmin}
              >
                Withdraw funds
              </Button>
            </Tooltip>
          ) : (
            <Button
              className="btn withdraw"
              type="primary"
              onClick={() => setShowWithdrawModal(true)}
              disabled={disabled || isAdmin}
            >
              Withdraw funds
            </Button>
          )}
          <Dropdown
            open={paymentMethodDropdownOpened}
            onOpenChange={setPaymentMethodDropdownOpened}
            trigger={["click"]}
            rootClassName="referral-outsiders-payment-method-dropdown"
            getPopupContainer={() => document.getElementById("main-content")}
            dropdownRender={() => (
              <Flex gap={4} vertical>
                <Button
                  type="text"
                  className="btn"
                  icon={<Icon className="btn-icon" type="arrows_rotate" />}
                  onClick={() => openPaymentMethodForm("edit")}
                  disabled={isAdmin}
                >
                  Change payment method
                </Button>
                <Button
                  type="text"
                  className="btn"
                  icon={<Icon className="btn-icon" type="eye_sparkle" />}
                  onClick={openPaymentMethodDetails}
                >
                  View details
                </Button>
              </Flex>
            )}
          >
            <Button
              type="default"
              className="btn dots"
              icon={<Icon className="dropdown-trigger-icon" type="three_dots" />}
            />
          </Dropdown>
        </Flex>
      ) : (
        <Button
          className="btn"
          type="primary"
          onClick={() => openPaymentMethodForm("add")}
          disabled={isAdmin}
        >
          Add payment method
        </Button>
      )}

      <WithdrawFundsModal open={showWithdrawModal} close={() => setShowWithdrawModal(false)} />
      <PaymentMethodDetailsModal
        open={paymentMethodDetailsOpened}
        close={() => setPaymentMethodDetailsOpened(false)}
        onChangePaymentMethod={() => {
          setPaymentMethodDetailsOpened(false)
          setPaymentMethodFormOpened(true)
          setPaymentMethodFormType("edit")
        }}
      />
      <PaymentMethodFormModal
        open={paymentMethodFormOpened}
        type={paymentMethodFormType}
        close={() => setPaymentMethodFormOpened(false)}
      />
    </Flex>
  )
}

export default EarningsStats
